import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { Modal, WithModal } from "@amzn/stencil-react-components/modal";
import { FormProvider } from "react-hook-form";
import React from "react";
import { AddressSuggestionsModal } from "@/common/components/AddressSuggestionsModal/address-suggestions-modal";
import { CancelOrderModal } from "@/pages/Checkout/components/CancelOrderModal/cancel-order-modal";
import { GetManagerApprovalCard } from "@/pages/Checkout/components/GetManagerApprovalCard/get-manager-approval-card";
import { ChangeShippingDetailsCard } from "@/pages/Checkout/components/ChangeShippingDetailsCard/change-shipping-details-card";
import { PolicyConfirmation } from "@/pages/Checkout/components/PolicyConfirmation/policy-confirmation";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { IItemVersion } from "@/common/types/item.types";
import { useCheckoutFormPanel } from "@/pages/Checkout/components/CheckoutFormPanel/hooks/use-checkout-form-panel.hook";

const STRING_PATH = "checkout.formPanel";

export interface CheckoutFormPanelProps {
  selectedVersion: IItemVersion;
}

export const CheckoutFormPanel: React.FC<CheckoutFormPanelProps> = ({ selectedVersion }) => {
  const {
    addressSuggestionsModal,
    form,
    handleAddressSuggestionsModalClose,
    handleCancelOrder,
    handleFormSubmit,
    handleValidAddress,
    isSubmitting,
    isValid,
    submissionError,
  } = useCheckoutFormPanel(selectedVersion);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit}>
        <Col width="100%" data-testid="checkout-form-panel">
          <Container paddingHorizontal={80} paddingTop={32} paddingBottom={32} isFullWidth={true}>
            <Col gridGap="S400" width="100%">
              <Text fontSize="T500" fontWeight="bold" color="neutral90">
                {getString(`${STRING_PATH}.checkout`)}
              </Text>
              <MessageBanner data-testid="checkout-banner" isDismissible={true}>
                {getString(`${STRING_PATH}.banner`)}
              </MessageBanner>
              <Text fontSize="T200" color="neutral90">
                {getString(`${STRING_PATH}.requiredField`)}
              </Text>

              <GetManagerApprovalCard />

              <ChangeShippingDetailsCard />

              <PolicyConfirmation />

              {submissionError && (
                <MessageBanner data-testid="checkout-error-banner" type={MessageBannerType.Error}>
                  {submissionError}
                </MessageBanner>
              )}
              <Row gridGap="S300" display="flex" justifyContent="flex-end">
                <WithModal
                  renderModal={({ close }) => (
                    <CancelOrderModal close={close} onConfirm={handleCancelOrder} />
                  )}
                  isScrollable={false}
                >
                  {({ open }) => (
                    <Button
                      data-testid="cancel-order-button"
                      variant={ButtonVariant.Tertiary}
                      onClick={open}
                    >
                      {getString(`${STRING_PATH}.cancel`)}
                    </Button>
                  )}
                </WithModal>
                <Button
                  data-testid="submit-order-button"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  variant={ButtonVariant.Primary}
                >
                  {isSubmitting
                    ? getString(`${STRING_PATH}.submittingOrder`)
                    : getString(`${STRING_PATH}.submitOrder`)}
                </Button>
              </Row>
            </Col>
          </Container>
        </Col>
      </form>
      <Modal
        isOpen={addressSuggestionsModal !== null}
        close={handleAddressSuggestionsModalClose}
        isScrollable={false}
      >
        {addressSuggestionsModal && (
          <AddressSuggestionsModal
            close={handleAddressSuggestionsModalClose}
            mode={addressSuggestionsModal.mode}
            enteredAddress={addressSuggestionsModal.enteredAddress}
            suggestion={addressSuggestionsModal.suggestion}
            handleValidAddress={handleValidAddress}
          />
        )}
      </Modal>
    </FormProvider>
  );
};
